import { LeftOutlined } from '@ant-design/icons'
import { Button, Flex, Form } from 'antd'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useStocksServiceCreateReturnEndpoint } from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { FormFooter, message, PageBreadcrumbs } from 'src/components'
import { ReturnFormFields } from 'src/features/stock/components'
import { useFormStyles } from 'src/features/stock/styles'
import {
  useFileHandler,
  useScrollToFirstError,
  ValidationError
} from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

type MedicineProps = {
  inventoryItemId: string
  quantity: number
  unitPrice: number
}

type ChangeValuesProps = {
  medicine: MedicineProps[]
}

export const StockReturnsCreatePage = () => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const [form] = Form.useForm()
  const { styles } = useFormStyles()
  const { onScrollToError } = useScrollToFirstError(form)

  const breadcrumbItems = useMemo(
    () => [
      { title: t('menu.consumable_medicaments') },
      { title: t('stock.returns') },
      { title: t('stock_returns.create_new_return') }
    ],
    [t]
  )

  const { onFileChange, saveFiles, isUploading } = useFileHandler({
    form,
    folder: `returns`
  })

  const { mutate, isPending } = useStocksServiceCreateReturnEndpoint({
    onSuccess: () => {
      message.success(t('stock_returns.return_added_success'))
      navigate(paths.stock.returns.root)
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const onBack = () => {
    form.resetFields()
    navigate(paths.stock.returns.root)
  }

  const updateTotalPrice = () => {
    const medicines = form?.getFieldValue('medicine') || []

    const updatedMedicines = medicines.map((med: MedicineProps) => {
      return {
        ...med,
        priceTotal:
          med?.quantity && med?.unitPrice
            ? Math.round(med.quantity * med.unitPrice * 100) / 100
            : undefined
      }
    })
    form?.setFieldsValue({ medicine: updatedMedicines })
  }

  const onValuesChange = (changedValues: ChangeValuesProps) => {
    if (changedValues.medicine) {
      updateTotalPrice()
    }
  }

  const onSave = async () => {
    try {
      const values = await form.validateFields()

      const items = values?.medicine

      const updatedFiles = await saveFiles()

      const payload = {
        contractId: values?.contractId,
        documentNumber: values?.invoiceNumber,
        documentDate: dayjs(values?.invoiceDate).format(yearMonthDaySlash),
        takeoverDate: dayjs(values?.takeoverDate).format(yearMonthDaySlash),
        transporterId: values?.transporterId,
        accompanyingNotice: values?.transporterAccompanyingNotice,
        comment: values?.comment,
        items,
        reasons: values?.reasons,
        mediaItems: updatedFiles
      }

      mutate({ requestBody: payload })
    } catch (error) {
      onScrollToError(error as ValidationError)
    }
  }

  return (
    <PageBreadcrumbs
      items={breadcrumbItems}
      title={t('stock_returns.create_new_return')}
    >
      <Flex vertical gap={16} className={styles.form}>
        <Flex>
          <Button icon={<LeftOutlined />} onClick={onBack}>
            {t('common.back')}
          </Button>
        </Flex>

        <Form
          layout="vertical"
          requiredMark={false}
          form={form}
          onValuesChange={onValuesChange}
          scrollToFirstError
        >
          <ReturnFormFields form={form} onChange={onFileChange} />

          <FormFooter
            onCancel={onBack}
            onSave={onSave}
            saveLabel={t('stock_returns.return')}
            saveConfirmTitle={`${t('stock_returns.return')}?`}
            loading={isUploading || isPending}
          />
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
